import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, PiggyBank, ArrowLeftRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import BankAccountForm from '../components/BankAccountForm';
import PageSuggestion from '../components/PageSuggestion';
import TransferModal from '../components/TransferModal';
import { bankAccountApi } from '../services/api';
import { BankAccount, CreateBankAccountRequest, UpdateBankAccountRequest, BankAccountTransferRequest } from '../types';

const BankAccounts: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
    const [filteredBankAccounts, setFilteredBankAccounts] = useState<BankAccount[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingBankAccount, setEditingBankAccount] = useState<BankAccount | null>(null);
    const [loading, setLoading] = useState(true);
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [transferSubmitting, setTransferSubmitting] = useState(false);
    const [transferError, setTransferError] = useState<string | null>(null);

    const itemsPerPage = 10;

    useEffect(() => {
        fetchBankAccounts();
    }, []);

    useEffect(() => {
        // Check if search param is provided in URL
        const searchParam = searchParams.get('search');
        if (searchParam) {
            setSearchTerm(searchParam);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const filtered = bankAccounts.filter(account =>
            account.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredBankAccounts(filtered);
        setCurrentPage(1);
    }, [searchTerm, bankAccounts]);

    const fetchBankAccounts = async () => {
        try {
            setLoading(true);
            const response = await bankAccountApi.getAll();
            setBankAccounts(response.data || []);
        } catch (error) {
            console.error('Erro ao buscar contas bancárias:', error);
            setBankAccounts([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (data: CreateBankAccountRequest) => {
        try {
            await bankAccountApi.create(data);
            await fetchBankAccounts();
            setIsModalOpen(false);
        } catch (error) {
            console.error('Erro ao criar conta bancária:', error);
        }
    };

    const handleUpdate = async (data: UpdateBankAccountRequest) => {
        if (!editingBankAccount) return;

        try {
            await bankAccountApi.update(editingBankAccount.id, data);
            await fetchBankAccounts();
            setIsModalOpen(false);
            setEditingBankAccount(null);
        } catch (error) {
            console.error('Erro ao atualizar conta bancária:', error);
        }
    };

    const handleEdit = (bankAccount: BankAccount) => {
        setEditingBankAccount(bankAccount);
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        setEditingBankAccount(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingBankAccount(null);
    };

    const handleOpenTransferModal = () => {
        setTransferError(null);
        setIsTransferModalOpen(true);
    };

    const handleCloseTransferModal = () => {
        setIsTransferModalOpen(false);
        setTransferError(null);
    };

    const handleTransfer = async (data: BankAccountTransferRequest) => {
        try {
            setTransferSubmitting(true);
            setTransferError(null);
            await bankAccountApi.transfer(data);
            await fetchBankAccounts();
            handleCloseTransferModal();
        } catch (err: unknown) {
            const axiosError = err as { response?: { data?: { translatedMessage?: string } } };
            const message = axiosError?.response?.data?.translatedMessage || 'Falha ao realizar a transferência.';
            setTransferError(message);
        } finally {
            setTransferSubmitting(false);
        }
    };

    const handleSubmit = async (data: CreateBankAccountRequest | UpdateBankAccountRequest) => {
        if (editingBankAccount) {
            await handleUpdate(data as UpdateBankAccountRequest);
        } else {
            await handleCreate(data as CreateBankAccountRequest);
        }
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value / 100 || 0);
    };

    const columns = [
        { key: 'name', label: 'Nome' },
        {
            key: 'initialBalance',
            label: 'Saldo Inicial',
            render: (value: number) => formatCurrency(value)
        },
        {
            key: 'currentBalance',
            label: 'Saldo Atual',
            render: (value: number) => formatCurrency(value)
        },
        {
            key: 'createdAt',
            label: 'Criado em',
            render: (value: string) => new Date(value).toLocaleDateString('pt-BR')
        }
    ];

    const totalPages = Math.ceil(filteredBankAccounts.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedData = filteredBankAccounts.slice(startIndex, startIndex + itemsPerPage);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-secondary-600">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-3 xl:flex-row xl:justify-between xl:items-start">
                <div>
                    <h1 className="text-2xl font-bold text-secondary-900">Contas Bancárias</h1>
                    <p className="text-secondary-600">Gerencie suas contas bancárias e saldos</p>
                    <PageSuggestion>
                        Cadastre cada conta que participa do seu fluxo financeiro para acompanhar saldo, transferencias e origem dos lancamentos.
                    </PageSuggestion>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleOpenTransferModal}
                        className="btn-secondary flex items-center"
                    >
                        <ArrowLeftRight className="h-4 w-4 mr-2" />
                        Transferências entre contas
                    </button>
                    <button
                        onClick={handleAddNew}
                        className="btn-primary flex items-center"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Adicionar Conta
                    </button>
                </div>
            </div>

            <DataTable
                data={paginatedData}
                columns={columns}
                searchPlaceholder="Buscar por nome..."
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onEdit={handleEdit}
                editButtonText="Editar"
                actionsRenderer={(item: BankAccount) => (
                    <div className="flex items-center gap-2">
                        {item.isVault && (
                            <Link
                                to={`/investments?bankAccountId=${item.id}`}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                                title="Gerenciar Investimentos"
                            >
                                <PiggyBank className="w-4 h-4" />
                                Investimentos
                            </Link>
                        )}
                    </div>
                )}
            />

            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={editingBankAccount ? 'Editar Conta Bancária' : 'Adicionar Conta Bancária'}
            >
                <BankAccountForm
                    bankAccount={editingBankAccount}
                    onSubmit={handleSubmit}
                    onCancel={handleCloseModal}
                />
            </Modal>

            <TransferModal
                isOpen={isTransferModalOpen}
                onClose={handleCloseTransferModal}
                bankAccounts={bankAccounts}
                onSubmit={handleTransfer}
                isSubmitting={transferSubmitting}
                submitError={transferError}
            />
        </div>
    );
};

export default BankAccounts;
